import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ORTSART_LABEL, ORTSARTEN, type Ortsart } from "@stammbaum/shared";
import { api, type OrtRegisterZeile } from "../api";
import { useTitel } from "../titel";

/**
 * Das Ortsregister.
 *
 * Orte entstehen im Vorbeigehen — wer eine Hochzeit einträgt, tippt einen
 * Ortsnamen, und die Kette entsteht von selbst. Hier fällt der Preis dafür
 * an: Dubletten, Tippfehler, und Orte, die noch unter keinem Bezirk hängen.
 *
 * Die Liste ist bewusst eine Liste und kein aufklappbarer Baum. Bei ein paar
 * hundert Orten sucht man den einen, den man gerade meint, und dafür ist
 * ein Suchfeld über allen Pfaden schneller als Klicken durch Ebenen. Die
 * Einrückung zeigt trotzdem, was worunter liegt.
 */
export default function Orte() {
  useTitel("Orte");
  const client = useQueryClient();
  const [suche, setSuche] = useState("");
  const [nurDoppelte, setNurDoppelte] = useState(false);
  const [nurUnbenutzte, setNurUnbenutzte] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  /** Erster Klick auf „alle löschen" fragt nach, zweiter führt aus. */
  const [loeschBereit, setLoeschBereit] = useState(false);

  // Wer an den Filtern dreht, meint etwas anderes als vorher — die scharfe
  // Nachfrage darf nicht stehen bleiben, sonst löscht der nächste einzelne
  // Klick sofort.
  useEffect(() => setLoeschBereit(false), [suche, nurDoppelte, nurUnbenutzte]);

  const { data, isPending } = useQuery({
    queryKey: ["ortsregister"],
    queryFn: api.ortsregister,
  });
  const { data: ich } = useQuery({ queryKey: ["ich"], queryFn: api.ich, staleTime: 5 * 60_000 });
  const darfAendern = ich?.rolle === "besitzer";

  const orte = data?.orte ?? [];
  const nachId = useMemo(() => new Map(orte.map((o) => [o.id, o])), [orte]);

  const sichtbar = useMemo(() => {
    const text = suche.trim().toLowerCase();
    return orte.filter(
      (o) =>
        (!nurDoppelte || o.doppelterName) &&
        (!nurUnbenutzte || (o.verwendungen === 0 && o.unterorte === 0)) &&
        (!text || o.pfad.toLowerCase().includes(text) || String(o.id) === text),
    );
  }, [orte, suche, nurDoppelte, nurUnbenutzte]);

  // Orte, an denen weder Person noch Ereignis noch Bild noch Unterort
  // haengt. Die entstehen im Betrieb - Tippfehler, zusammengefuehrte Ketten,
  // Adressen aus dem Altimport - und blaehen vor allem die Vorschlagslisten
  // der Masken auf.
  const unbenutzt = useMemo(
    () => orte.filter((o) => o.verwendungen === 0 && o.unterorte === 0).length,
    [orte],
  );

  const ort = gewaehlt === null ? null : (nachId.get(gewaehlt) ?? null);

  async function neuLaden() {
    await client.invalidateQueries({ queryKey: ["ortsregister"] });
    // Die Vorschlagslisten der Masken zeigen dieselben Orte.
    await client.invalidateQueries({ queryKey: ["orte"] });
  }

  /** Ruft die API und hält Fehler als Text fest, statt sie zu werfen. */
  async function versuche(was: () => Promise<string | null>) {
    setFehler(null);
    setHinweis(null);
    try {
      const meldung = await was();
      await neuLaden();
      if (meldung) setHinweis(meldung);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Fehlgeschlagen.");
    }
  }

  /** Wie tief der Ort in der Kette hängt — nur für die Einrückung. */
  const tiefe = (o: OrtRegisterZeile) => o.pfad.split(",").length - 1;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Orte</h1>
        <span className="text-sm text-black/50 dark:text-white/50">
          {orte.length} Einträge
          {orte.some((o) => o.doppelterName) &&
            ` · ${orte.filter((o) => o.doppelterName).length} mit doppeltem Namen`}
          {unbenutzt > 0 && ` · ${unbenutzt} unbenutzt`}
        </span>
      </div>

      {!darfAendern && (
        <p className="mb-4 rounded-lg border border-black/10 p-3 text-sm text-black/60 dark:border-white/10 dark:text-white/60">
          Ändern darf das Register nur der Besitzer — die Orte hängen an allen
          Personen, Ereignissen und Bildern gemeinsam.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="suchen … (Ort, Pfad oder Nummer)"
          className="flex-1 rounded-lg border border-black/15 bg-white px-3 py-2 outline-none
                     focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={nurDoppelte}
            onChange={(e) => setNurDoppelte(e.target.checked)}
          />
          nur doppelte Namen
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={nurUnbenutzte}
            onChange={(e) => setNurUnbenutzte(e.target.checked)}
          />
          nur unbenutzte
        </label>
        {/*
          Nur ohne Suchtext: der Knopf löscht ALLE unbenutzten Orte, nicht
          die gerade sichtbaren. Wer „Echsenbach" tippt, sähe drei Zeilen
          und löschte hundertzwanzig.
        */}
        {darfAendern && nurUnbenutzte && !suche.trim() && unbenutzt > 0 && (
          <button
            onClick={() => {
              // Zweistufig statt confirm(): der erste Klick sagt, was
              // passiert, der zweite tut es. Gelöscht wird nur, woran
              // nachweislich nichts hängt, und alles steht im Protokoll.
              if (!loeschBereit) {
                setLoeschBereit(true);
                return;
              }
              setLoeschBereit(false);
              void versuche(async () => {
                const e = await api.verwaisteOrteLoeschen();
                return (
                  `${e.geloescht} unbenutzte Orte gelöscht.` +
                  (e.danachVerwaist > 0
                    ? ` ${e.danachVerwaist} sind dadurch neu unbenutzt — noch einmal klicken, wenn die auch weg sollen.`
                    : "")
                );
              });
            }}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              loeschBereit
                ? "bg-red-700 text-white"
                : "border border-black/15 hover:bg-black/[0.04] dark:border-white/15 dark:hover:bg-white/5"
            }`}
          >
            {loeschBereit ? `Wirklich ${unbenutzt} Orte löschen?` : `alle ${unbenutzt} löschen`}
          </button>
        )}
      </div>

      {fehler && <p className="mb-3 text-sm text-red-700">{fehler}</p>}
      {hinweis && <p className="mb-3 text-sm text-green-700">{hinweis}</p>}

      {isPending && <p className="text-black/40">lädt …</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <ul className="divide-y divide-black/5 text-sm dark:divide-white/10">
          {sichtbar.map((o) => (
            <li key={o.id}>
              <button
                onClick={() => {
                  setGewaehlt(o.id === gewaehlt ? null : o.id);
                  setFehler(null);
                  setHinweis(null);
                }}
                className={`flex w-full items-baseline gap-2 py-1.5 text-left hover:bg-black/[0.03]
                            dark:hover:bg-white/5 ${o.id === gewaehlt ? "bg-black/[0.05] dark:bg-white/10" : ""}`}
              >
                <span style={{ paddingLeft: `${tiefe(o) * 1.1}rem` }} className="flex-1">
                  <span className="font-medium">{o.name}</span>
                  {o.plz && (
                    <span className="ml-2 text-black/40 dark:text-white/40">{o.plz}</span>
                  )}
                  {o.doppelterName && (
                    <span className="ml-2 text-amber-700" title="diesen Namen gibt es mehrfach">
                      doppelt
                    </span>
                  )}
                  <span className="ml-2 text-xs text-black/35 dark:text-white/35">
                    {o.pfad.split(", ").slice(1).join(", ")}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-xs text-black/40 dark:text-white/40">
                  {o.verwendungen}×{o.unterorte > 0 && ` · ${o.unterorte} darunter`}
                </span>
              </button>
            </li>
          ))}
          {!isPending && sichtbar.length === 0 && (
            <li className="py-3 text-black/50">Kein Ort passt dazu.</li>
          )}
        </ul>

        {ort && (
          <OrtFormular
            key={ort.id}
            ort={ort}
            orte={orte}
            darfAendern={darfAendern}
            versuche={versuche}
            onGeloescht={() => setGewaehlt(null)}
          />
        )}
      </div>
    </div>
  );
}

const feld =
  "mt-1 w-full rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm outline-none " +
  "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";
const beschriftung = "text-xs text-black/60 dark:text-white/60";

/** Der rechte Streifen: alles, was man an einem Ort ändern kann. */
function OrtFormular({
  ort,
  orte,
  darfAendern,
  versuche,
  onGeloescht,
}: {
  ort: OrtRegisterZeile;
  orte: OrtRegisterZeile[];
  darfAendern: boolean;
  versuche: (was: () => Promise<string | null>) => Promise<void>;
  onGeloescht: () => void;
}) {
  const [name, setName] = useState(ort.name);
  const [art, setArt] = useState<Ortsart>(ort.art);
  const [plz, setPlz] = useState(ort.plz ?? "");
  const [land, setLand] = useState(ort.land ?? "");
  const [breite, setBreite] = useState(ort.breitengrad?.toString() ?? "");
  const [laenge, setLaenge] = useState(ort.laengengrad?.toString() ?? "");
  const [elternteil, setElternteil] = useState<string>(ort.teilVonId?.toString() ?? "");
  const [ziel, setZiel] = useState("");

  const zahlOderNull = (t: string) => (t.trim() === "" ? null : Number(t.replace(",", ".")));

  // Als Elternteil kommt nicht in Frage, was unter diesem Ort hängt - das
  // gäbe eine Kette ohne Wurzel. Der Server prüft es auch, aber ein Eintrag,
  // den man gar nicht erst anbietet, erspart die Fehlermeldung.
  const untenDrunter = useMemo(() => {
    const ids = new Set([ort.id]);
    let gewachsen = true;
    while (gewachsen) {
      gewachsen = false;
      for (const o of orte) {
        if (o.teilVonId !== null && ids.has(o.teilVonId) && !ids.has(o.id)) {
          ids.add(o.id);
          gewachsen = true;
        }
      }
    }
    return ids;
  }, [orte, ort.id]);

  const moeglich = orte
    .filter((o) => !untenDrunter.has(o.id))
    .sort((a, b) => a.pfad.localeCompare(b.pfad, "de"));

  return (
    <aside className="h-fit rounded-lg border border-black/10 p-3 dark:border-white/10">
      <p className="mb-3 text-xs text-black/40 dark:text-white/40">
        Ort {ort.id} · {ort.verwendungen}× verwendet
      </p>

      <div className="grid gap-3">
        <label className="block">
          <span className={beschriftung}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={feld} />
        </label>

        <label className="block">
          <span className={beschriftung}>Ebene</span>
          <select value={art} onChange={(e) => setArt(e.target.value as Ortsart)} className={feld}>
            {ORTSARTEN.map((a) => (
              <option key={a} value={a}>
                {ORTSART_LABEL[a]}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={beschriftung}>PLZ</span>
            <input value={plz} onChange={(e) => setPlz(e.target.value)} className={feld} />
          </label>
          <label className="block">
            <span className={beschriftung}>Land</span>
            <input
              value={land}
              onChange={(e) => setLand(e.target.value)}
              maxLength={3}
              placeholder="AUT"
              className={feld}
            />
          </label>
        </div>

        <label className="block">
          <span className={beschriftung}>liegt in</span>
          <select
            value={elternteil}
            onChange={(e) => setElternteil(e.target.value)}
            className={feld}
          >
            <option value="">— nichts darüber —</option>
            {moeglich.map((o) => (
              <option key={o.id} value={o.id}>
                {o.pfad}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={beschriftung}>Breitengrad</span>
            <input
              value={breite}
              onChange={(e) => setBreite(e.target.value)}
              placeholder="48.6"
              className={feld}
            />
          </label>
          <label className="block">
            <span className={beschriftung}>Längengrad</span>
            <input
              value={laenge}
              onChange={(e) => setLaenge(e.target.value)}
              placeholder="15.1"
              className={feld}
            />
          </label>
        </div>

        <button
          disabled={!darfAendern}
          onClick={() =>
            versuche(async () => {
              await api.ortAendern(ort.id, {
                name: name.trim(),
                art,
                plz: plz.trim() || null,
                land: land.trim() || null,
                breitengrad: zahlOderNull(breite),
                laengengrad: zahlOderNull(laenge),
                teilVonId: elternteil === "" ? null : Number(elternteil),
              });
              return "Gespeichert.";
            })
          }
          className="rounded-lg border border-black/15 px-3 py-1.5 text-sm hover:border-black/40
                     disabled:opacity-40 dark:border-white/15"
        >
          Speichern
        </button>
      </div>

      <hr className="my-4 border-black/10 dark:border-white/10" />

      <p className={beschriftung}>Zusammenführen mit</p>
      <p className="mt-1 text-xs text-black/40 dark:text-white/40">
        Dieser Ort verschwindet, alles daran zeigt danach auf den gewählten.
        Unterorte mit gleichem Namen werden mit verschmolzen.
      </p>
      <select
        value={ziel}
        onChange={(e) => setZiel(e.target.value)}
        className={feld}
        disabled={!darfAendern}
      >
        <option value="">— auswählen —</option>
        {orte
          .filter((o) => o.id !== ort.id)
          .sort((a, b) => a.pfad.localeCompare(b.pfad, "de"))
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.pfad}
            </option>
          ))}
      </select>
      <button
        disabled={!darfAendern || ziel === ""}
        onClick={() =>
          versuche(async () => {
            const e = await api.orteZusammenfuehren(ort.id, Number(ziel));
            onGeloescht();
            return (
              `${e.orte} Ort(e) zusammengeführt` +
              (e.umgehaengt ? `, ${e.umgehaengt} umgehängt` : "") +
              (e.umbenannt ? `, Name bereinigt: ${e.umbenannt}` : "") +
              "."
            );
          })
        }
        className="mt-2 w-full rounded-lg border border-black/15 px-3 py-1.5 text-sm
                   hover:border-black/40 disabled:opacity-40 dark:border-white/15"
      >
        Zusammenführen
      </button>

      {ort.verwendungen === 0 && ort.unterorte === 0 && (
        <button
          disabled={!darfAendern}
          onClick={() =>
            versuche(async () => {
              await api.ortLoeschen(ort.id);
              onGeloescht();
              return "Ort entfernt.";
            })
          }
          className="mt-4 w-full text-sm text-black/40 hover:text-red-700 disabled:opacity-40
                     dark:text-white/40"
        >
          Ort löschen (wird nirgends verwendet)
        </button>
      )}
    </aside>
  );
}
